import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import Input from 'components/final-form/Input';
import TextArea from 'components/final-form/TextArea';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import PraxFormScrollableContainer from 'components/layout/PraxFormScrollableContainer';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import LoaderComponent from 'components/LoaderComponent';
import moment from 'moment';
import React, { useState } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Icon, List, Modal, Segment } from 'semantic-ui-react';
import { addPatientReportEmail, addPatientReportToCdr } from 'services/patientServices';
import styled from 'styled-components';
import { PatientDocumentType, PatientDto, PatientSaveDocumentRequest, PdfResponse } from 'ts-types/api.types';
import { composeValidators, multipleEmailsValidator, required } from 'utils/validatorUtils';


const StyledModal = styled(Modal)`
  height: 90vh;
  width: 90vw !important;
`;

const StyledModalContent = styled(Modal.Content)`
  padding: 0 !important;
  height: 100% !important;

  .ui.inline.loader.active.send-email-in-progress {
    margin-top: -0.25rem;
    margin-left: 0.375rem;
  }
`;

const StyledSegmentGroup = styled(Segment.Group)`
  margin: 0 !important;
  height: 100% !important;
  border: none !important;
`;

const LeftSegment = styled(Segment)`
  width: 70% !important;
  padding: 0 !important;
`;

const RightSegment = styled(Segment.Group)`
  width: 30% !important;
  margin: 0 !important;
  border: none !important;
`;

const ListSegment = styled(Segment)`
  padding: 0 !important;
`;

const ButtonSegment = styled(Segment)`
  height: 8% !important;
  min-height: 50px !important;
  padding-top: 7% !important;
  padding-bottom: 6% !important;
  align-items: center;
  border: none !important;
  box-shadow: none !important;

  & button {
    float: right;
  }
`;

const ListContent = styled.div`
  width: 90%;
  display: inline-block;
  align-items: center;
`;

const DownloadSection = styled.div`
  width: 10%;
  display: inline-block;
  align-items: center;
  vertical-align: top;
`;

const StyledListItem = styled(List.Item)`
  padding: 0.75rem !important;

  .file-name {
    word-break: break-all;
  }
`;

const StyledForm = styled.form`
  padding: 0 1rem;

  label {
    font-weight: bold;
  }
`;

const btnStyle = {
  background: "transparent",
  cursor: "pointer",
  padding: "5px 10px"
};

interface DocumentData {
  emailsTo: string,
  emailsCc: string,
  subject: string,
  messageText: string,
  savePdf: boolean
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  pdfResponses: Array<PdfResponse>
  modalOpen: boolean,
  onSave: (successfullySaved: boolean) => void,
  onSendEmail: (successfullySendEmail: boolean) => void,
  onCloseModal: () => void,
  getPdfUrl: (pdfResponse: PdfResponse) => string,

  sendEmailTextKey: string,
  doctorEmail: string,
  doctorId: number,
  patient: PatientDto,
  pdfDocumentDescription: string,
  documentType: PatientDocumentType,
  treatmentId: number,
  reportDto: any,
  reportId?: number
}

const PatientReportPdfModal = (props: Props) => {

  const {
    pdfResponses,
    modalOpen,
    onCloseModal,
    onSave,
    onSendEmail,
    getMessage,
    getPdfUrl,
    doctorEmail,
    doctorId,
    patient,
    sendEmailTextKey,
    pdfDocumentDescription,
    documentType,
    currentUser,
    options,
    treatmentId,
    reportDto,
    reportId
  } = props;

  if (!pdfResponses || pdfResponses.length == 0) {
    onCloseModal();
  }

  const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  const [selectedPdfResponse, setSelectedPdfResponse] = useState<PdfResponse>(pdfResponses[0]);
  const [errorMessage, setErrorMessage] = useState(undefined as string | undefined);
  const [loader, setLoader] = useState(false);

  const pdfUrl: string = getPdfUrl(selectedPdfResponse);

  const initialDocumentFormValues: DocumentData = {
    emailsTo: doctorEmail ? doctorEmail : "",
    emailsCc: "",
    subject: "",
    messageText: "",
    savePdf: true
  }

  const handleSubmit = async (values: any) => {

    setLoader(true);

    const request: Partial<PatientSaveDocumentRequest> = {
      ...reportDto,
      pdfId: pdfResponses[0].pdfId,
      name: `${pdfResponses[0].pdfName}`,
      type: documentType,
      description: pdfDocumentDescription,
      patientId: patient.id,
      documentDate: moment().format("YYYY-MM-DD"),
      doctorId: doctorId,
      fileName: `${pdfResponses[0].pdfName}`,
      archived: false,
      documentId: reportId,
      emailsTo: values.emailsTo,
      emailsCc: values.emailsCc,
      subject: values.subject,
      messageText: values.messageText,
      mandateId: currentUser!.userConfig!.mandateId
    }

    try {
      await addPatientReportEmail(request, cancelTokenSource);
    } finally {
      onSendEmail(true);
      setLoader(false);
    }
  }

  const handleError = (error: any) => {
    const errorCode = error.errorCode;
    const knownErrors: Array<String> = [];
    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        setErrorMessage(getMessage('error.general'));
      }
    }
  };

  const savePatientSynopsisReport = async () => {

    const savePdfDocument: Partial<PatientSaveDocumentRequest> = {
      ...reportDto,
      pdfId: pdfResponses[0].pdfId,
      name: `${pdfResponses[0].pdfName}`,
      type: documentType,
      description: pdfDocumentDescription,
      patientId: patient.id,
      documentDate: moment().format("YYYY-MM-DD"),
      doctorId: doctorId,
      fileName: `${pdfResponses[0].pdfName}`,
      archived: false,
      documentId: reportId
    }

    setLoader(true);

    addPatientReportToCdr(savePdfDocument, cancelTokenSource)
        .then(response => {
          onSave(true)
        })
        .catch((e: any) => handleError(e.response.data))
        .finally(() => {
          setLoader(false)
        })

  }

  const mappedPdfResponses = pdfResponses.map((pdfResponse, index) => (
      <StyledListItem
          key={index}
          active={selectedPdfResponse.pdfId == pdfResponse.pdfId}
          onClick={() => setSelectedPdfResponse(pdfResponse)}
      >
        <List.Icon style={{padding: "10px"}} name="file pdf outline" size="large" verticalAlign="middle"/>
        <List.Content>
          <ListContent>
            <List.Header as="h4">{pdfResponse.pdfName}</List.Header>
            <List.Description as="span">{pdfResponse.pdfSize}</List.Description>
          </ListContent>
          <DownloadSection>
            <Button
                as="a"
                href={getPdfUrl(pdfResponse)}
                download={pdfResponse.pdfName}
                icon
                style={btnStyle}
            >
              <Icon name="download" size="large"/>
            </Button>
          </DownloadSection>
        </List.Content>
      </StyledListItem>
  ));

  const renderFinalForm = () => {
    return (
        <FinalForm
            onSubmit={(values) => handleSubmit(values)}
            subscription={{pristine: true, submitting: true, values: true}}
            initialValues={initialDocumentFormValues}
            render={renderForm}
        />
    );
  }

  const renderForm = ({values, handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const validators = composeValidators(required, multipleEmailsValidator);

    return (
        <StyledForm onSubmit={handleSubmit}>
          {
              errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
          }
          <FormContainer>
            <FormContent>
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={2}>
                    <label htmlFor="emailsTo">{getMessage("synopsisReport.pdfModal.email.to")}</label>
                  </Grid.Column>
                  <Grid.Column width={14}>
                    <Field
                        name="emailsTo"
                        component={Input}
                        validate={validators}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={2}>
                    <label htmlFor="emailsCc">{getMessage("synopsisReport.pdfModal.email.cc")}</label>
                  </Grid.Column>
                  <Grid.Column width={14}>
                    <Field
                        name="emailsCc"
                        component={Input}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={2}>
                    <label htmlFor="subject">{getMessage("synopsisReport.pdfModal.email.subject")}</label>
                  </Grid.Column>
                  <Grid.Column width={14}>
                    <Field
                        name="subject"
                        component={Input}
                        fluid
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={2}>
                    <label htmlFor="messageText">{getMessage("synopsisReport.pdfModal.email.message")}</label>
                  </Grid.Column>
                  <Grid.Column width={14}>
                    <Field
                        name="messageText"
                        component={TextArea}
                        style={{minHeight: "20vh", maxHeight: "50vh"}}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column>
                    <Button compact type="submit" primary disabled={submitting}>
                      {getMessage(sendEmailTextKey)}
                    </Button>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <ButtonSegment>
                      <Button compact
                              primary
                              type="button"
                              onClick={savePatientSynopsisReport}
                      >
                        {getMessage("button.saveAndClose")}
                      </Button>
                      <Button compact
                              type="button"
                              onClick={onCloseModal}
                      >
                        {getMessage("button.close")}
                      </Button>
                    </ButtonSegment>
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </FormContent>
          </FormContainer>
        </StyledForm>
    );
  }

  return (
      <StyledModal
          open={modalOpen}
          onClose={onCloseModal}
      >
        <StyledModalContent>
          <StyledSegmentGroup horizontal>
            <LeftSegment>
              <embed
                  src={pdfUrl}
                  width="100%"
                  height="100%"
                  type="application/pdf"
              />
            </LeftSegment>
            <RightSegment>
              {loader
                  ? <LoaderComponent message={getMessage("loading")}/>
                  : <PraxFormScrollableContainer height={"85rem"}>
                    <ListSegment>
                      <List divided relaxed selection>
                        {mappedPdfResponses}
                      </List>
                    </ListSegment>
                    {renderFinalForm()}
                  </PraxFormScrollableContainer>
              }
            </RightSegment>
          </StyledSegmentGroup>
        </StyledModalContent>
      </StyledModal>
  );

}

export default withMessages(withAuthContext(PatientReportPdfModal));


